import React, { useEffect } from "react";
import { array, bool, func, number } from "prop-types";
import { useDispatch } from "react-redux";

import { useTranslation } from "@lib/i18n";

import RegistrationForm from "./RegistrationForm";
import RegistrationSummary from "./RegistrationSummary";
import RegistrationUnavailable from "./RegistrationUnavailable";
import CollapsableContainer from "@components/shared/CollapsableContainer";

import { useRegistrationAvailabilityGate } from "@hooks/useRegistrationAvailabilityGate";
import { REGISTRATION_AVAILABILITY_STATUS } from "@shared/constants";

import { dealType } from "@types";
import { getIsVehicleRegistrationByCitroenSelected } from "@shared/v2/lib/getIsVehicleRegistrationByCitroenSelected";

import { setVehicleRegistrationRegionAction } from "@redux/actions/dealActions";

const VehicleRegistration = ({
  isSummary,
  availableMethods,
  disabled,
  index,
  isOpen,
  deal,
  openVehicleRegistration,
  closeVehicleRegistration,
  onVehicleRegistrationSelected,
  onVehicleRegistrationConfirmed,
  updateVehicleRegistration,
  initialGTMSent,
  fixedHeaderHeight,
  openRegistrationCostChangedModal,
}) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();

  const {
    vehicleRegistration,
    vehicleRegistrationRegion,
  } = deal.financeSimulation;
  const isRegistrationByCitroen = getIsVehicleRegistrationByCitroenSelected(
    vehicleRegistration
  );

  const availabilityGate = useRegistrationAvailabilityGate();
  const isRegistrationUnavailable =
    !isSummary &&
    availableMethods?.length === 0 &&
    availabilityGate.status !== REGISTRATION_AVAILABILITY_STATUS.OK;

  useEffect(() => {
    if (isRegistrationByCitroen) {
      return;
    }
    if (!vehicleRegistrationRegion) {
      return;
    }
    dispatch(setVehicleRegistrationRegionAction(null));
  }, [isRegistrationByCitroen, vehicleRegistrationRegion]);

  return (
    <CollapsableContainer
      isDisabled={disabled}
      isOpen={isOpen}
      onChange={isOpen =>
        isOpen ? closeVehicleRegistration?.() : openVehicleRegistration?.()
      }
      title={t("basket.vehicleRegistration.title")}
      titleIndex={index}
    >
      {isRegistrationUnavailable && (
        <RegistrationUnavailable
          status={availabilityGate.status}
          otherAvailableJourneys={availabilityGate.otherAvailableJourneys}
          switchVariant={availabilityGate.switchVariant}
        />
      )}
      {!isRegistrationUnavailable && isSummary && (
        <RegistrationSummary deal={deal} />
      )}
      {!isRegistrationUnavailable && !isSummary && (
        <RegistrationForm
          deal={deal}
          availableMethods={availableMethods}
          disabled={disabled}
          fixedHeaderHeight={fixedHeaderHeight}
          initialGTMSent={initialGTMSent}
          onVehicleRegistrationSelected={onVehicleRegistrationSelected}
          onVehicleRegistrationConfirmed={onVehicleRegistrationConfirmed}
          updateVehicleRegistration={updateVehicleRegistration}
          openRegistrationCostChangedModal={openRegistrationCostChangedModal}
        />
      )}
    </CollapsableContainer>
  );
};

VehicleRegistration.propTypes = {
  index: number.isRequired,
  isSummary: bool,
  disabled: bool,
  isOpen: bool,
  openVehicleRegistration: func,
  closeVehicleRegistration: func,
  deal: dealType.isRequired,
  availableMethods: array.isRequired,
  onVehicleRegistrationSelected: func.isRequired,
  onVehicleRegistrationConfirmed: func.isRequired,
  updateVehicleRegistration: func,
  initialGTMSent: bool,
  fixedHeaderHeight: number,
  openRegistrationCostChangedModal: func,
};

export default VehicleRegistration;
